import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

const MODAL_TRANSITION_MS = 180;

interface AppModalProps {
  open: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  titleId?: string;
  aiIdPrefix?: string;
}

export function AppModal({
  open,
  onRequestClose,
  children,
  titleId,
  aiIdPrefix = 'app-modal',
}: AppModalProps) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onRequestClose();
      }
    },
    [onRequestClose, open],
  );

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const animationFrame = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(animationFrame);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => setShouldRender(false), MODAL_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-ai-id={aiIdPrefix}
    >
      <button
        type="button"
        aria-label="닫기"
        className={`absolute inset-0 h-full w-full bg-slate-950/40 transition-opacity duration-200 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onRequestClose}
        data-ai-id={`${aiIdPrefix}-backdrop-button`}
      />

      <div
        className={`relative z-10 w-full max-w-[560px] rounded-[28px] bg-white shadow-2xl transition duration-200 ease-out ${
          isVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0'
        }`}
        data-ai-id={`${aiIdPrefix}-panel`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
